import { useState } from "react";
import PageHero from "@/components/feature/PageHero";
import CtaSection from "@/components/feature/CtaSection";
import Reveal from "@/components/base/Reveal";
import { ownerBenefits, agencyBenefits, agencyChain, solutionTabs } from "@/mocks/public";
import { cn } from "@/utils/cn";

const agencyExtras = [
  { icon: "ri-shield-user-line", title: "Gestion des employés", description: "Ajoutez vos collaborateurs et attribuez-leur des autorisations précises." },
  { icon: "ri-lock-password-line", title: "Autorisations", description: "Contrôlez qui peut consulter, créer, modifier ou supprimer les données." },
  { icon: "ri-group-2-line", title: "Suivi multi-propriétaires", description: "Séparez les immeubles, les loyers et les revenus de chaque propriétaire." },
  { icon: "ri-megaphone-line", title: "Signalement", description: "Remontez les incidents et les besoins d'entretien au bon interlocuteur." },
];

export default function SolutionsPage() {
  const [tab, setTab] = useState("proprietaires");
  const isOwner = tab === "proprietaires";

  return (
    <div className="flex flex-col">
      <PageHero
        eyebrow="Solutions"
        current="Solutions"
        title="Une réponse adaptée à votre profil"
        description="Propriétaire d'un ou plusieurs immeubles ou agence immobilière gérant plusieurs clients : choisissez l'espace qui vous correspond."
      />

      <section className="py-14 md:py-20 bg-background-50">
        <div className="container-page">
          <Reveal className="flex justify-center">
            <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              {solutionTabs.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setTab(item.id)}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-label font-medium transition-all duration-200 cursor-pointer",
                    tab === item.id ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                  )}
                >
                  <i className={item.icon} aria-hidden="true" />
                  {item.label}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
            <Reveal variant="left">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
                {isOwner ? "Pour les propriétaires" : "Pour les agences immobilières"}
              </h2>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                {isOwner
                  ? "Un espace unique pour suivre votre patrimoine, vos locataires et vos revenus sans dépendre d'un cahier."
                  : "Un espace structuré autour de vos propriétaires, de leurs immeubles et de vos employés."}
              </p>
              <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(isOwner ? ownerBenefits : agencyBenefits).map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            {isOwner ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                  { icon: "ri-building-2-line", title: "Immeubles et biens", description: "Organisez votre patrimoine par immeuble, étage et type de bien." },
                  { icon: "ri-user-3-line", title: "Locataires et contrats", description: "Centralisez les fiches locataires et les clauses de chaque contrat." },
                  { icon: "ri-wallet-3-line", title: "Loyers et impayés", description: "Suivez les encaissements, les retards et les montants dus." },
                  { icon: "ri-bar-chart-box-line", title: "Statistiques", description: "Mesurez vos revenus et le taux d'occupation de vos biens." },
                ].map((item, index) => (
                  <Reveal key={item.title} delay={index * 80} className="h-full">
                    <div className="card-lift h-full rounded-lg border border-background-200 bg-background-100/60 p-5 hover:border-primary-300 hover:bg-background-50">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                        <i className={`${item.icon} text-lg`} aria-hidden="true" />
                      </span>
                      <h3 className="mt-3 text-sm font-heading font-semibold text-foreground-950">{item.title}</h3>
                      <p className="mt-1.5 text-sm text-foreground-600">{item.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <Reveal variant="right" className="rounded-lg border border-background-200 bg-background-100/60 p-5">
                  <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-500">
                    Chaîne de gestion
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {agencyChain.map((node, index) => (
                      <div key={node.label} className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-label font-medium text-foreground-800">
                          <i className={node.icon} aria-hidden="true" />
                          {node.label}
                        </span>
                        {index < agencyChain.length - 1 && (
                          <i className="ri-arrow-right-line text-foreground-400" aria-hidden="true" />
                        )}
                      </div>
                    ))}
                  </div>
                </Reveal>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {agencyExtras.map((item, index) => (
                    <Reveal key={item.title} delay={index * 80} className="h-full">
                      <div className="card-lift h-full rounded-lg border border-background-200 bg-background-100/60 p-5 hover:border-accent-300 hover:bg-background-50">
                        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                          <i className={`${item.icon} text-lg`} aria-hidden="true" />
                        </span>
                        <h3 className="mt-3 text-sm font-heading font-semibold text-foreground-950">{item.title}</h3>
                        <p className="mt-1.5 text-sm text-foreground-600">{item.description}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <CtaSection
        title={isOwner ? "Reprenez la main sur votre patrimoine." : "Structurez la gestion de votre agence."}
        description="Créez votre espace dès aujourd'hui et adaptez la formule à votre activité."
      />
    </div>
  );
}